import {
  AppCredentialConfig,
  AppCredentialConfigSaveRequest,
  ClientPlatform,
  MerchantConfig,
  PAYMENT_PROVIDER_MAP,
  PaymentProvider,
  PlatformPaymentConfig,
  PlatformPaymentRoute,
} from '../types';

export type SelectOption = {
  label: string;
  value: number;
};

export type RouteScopeIssue = 'conflict' | 'empty_scope';

/**
 * 约束阶段完成前写死关闭；批次 B 与后端开关打通后再改为读配置。
 * 关闭时禁止为同一平台+真实 AppID 创建第二服务商候选资质。
 */
export const MULTI_PROVIDER_CANDIDATES_ENABLED = true;

export const toNumberId = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : undefined;
};

export const idsEqual = (left: unknown, right: unknown): boolean => {
  const leftId = toNumberId(left);
  const rightId = toNumberId(right);
  if (leftId === undefined || rightId === undefined) {
    return left === right;
  }
  return leftId === rightId;
};

export function isAppScopedPlatform(platform?: ClientPlatform): boolean {
  return platform === 'WEAPP' || platform === 'ALIPAY';
}

export function formatAppCredentialLabel(
  config: Pick<AppCredentialConfig, 'appCredentialId' | 'appName' | 'appId'> & {
    paymentProvider?: PaymentProvider | string;
  },
): string {
  const name = config.appName?.trim() || `应用资质 ${config.appCredentialId}`;
  const appId = config.appId?.trim() || '-';
  const provider = config.paymentProvider
    ? PAYMENT_PROVIDER_MAP[config.paymentProvider as PaymentProvider] || config.paymentProvider
    : GENERIC_APP_CREDENTIAL_LABEL;
  return `${name} / ${appId} / ${provider}`;
}

export const GENERIC_APP_CREDENTIAL_LABEL = '通用';

export function isGenericAppCredential(config: Pick<AppCredentialConfig, 'paymentProvider'>): boolean {
  return !config.paymentProvider;
}

/** 同一商户号下的多个云汇腾子商户靠云汇腾应用 ID 区分，标签中必须带上。 */
export function formatMerchantLabel(
  config: Pick<MerchantConfig, 'merchantConfigId' | 'name' | 'merchantNo' | 'sellerId'> &
    Partial<Pick<MerchantConfig, 'paymentProvider' | 'gatewayAppId' | 'effectiveGatewayAppId'>>,
): string {
  const name = config.name?.trim() || `商户/收款账号 ${config.merchantConfigId}`;
  const account = config.merchantNo?.trim() || config.sellerId?.trim();
  const parts = account ? [name, account] : [name];
  if (config.paymentProvider === 'YUNHUITENG') {
    const gatewayAppId = config.gatewayAppId?.trim() || config.effectiveGatewayAppId?.trim();
    parts.push(gatewayAppId ? `应用ID ${gatewayAppId}` : '未填写云汇腾应用ID');
  }
  return parts.join(' / ');
}

/** 应用资质表单内代表“通用”的占位值，提交时转换为不传服务商。 */
export const GENERIC_PROVIDER_VALUE = 'GENERIC';
export type AppCredentialProviderChoice = PaymentProvider | typeof GENERIC_PROVIDER_VALUE;
export type AppCredentialFormValues = Omit<AppCredentialConfigSaveRequest, 'paymentProvider'> & {
  paymentProvider?: AppCredentialProviderChoice;
};

export function toAppCredentialSavePayload(values: AppCredentialFormValues): AppCredentialConfigSaveRequest {
  const { paymentProvider, ...rest } = values;
  if (!paymentProvider || paymentProvider === GENERIC_PROVIDER_VALUE) {
    const next: AppCredentialConfigSaveRequest = { ...rest };
    delete next.gatewayAppId;
    return next;
  }
  return { ...rest, paymentProvider };
}

/** 云汇腾应用 ID 展示：商户配置自身的值优先，否则展示当前下单实际使用的存量值（inherited）。 */
export function describeMerchantGatewayAppId(
  config: Pick<MerchantConfig, 'paymentProvider' | 'gatewayAppId' | 'effectiveGatewayAppId'>,
): { value?: string; inherited: boolean } {
  if (config.paymentProvider !== 'YUNHUITENG') {
    return { inherited: false };
  }
  if (config.gatewayAppId) {
    return { value: config.gatewayAppId, inherited: false };
  }
  return { value: config.effectiveGatewayAppId, inherited: Boolean(config.effectiveGatewayAppId) };
}

export function getSaveWarnings(data: unknown): string[] {
  if (!Array.isArray(data)) {
    return [];
  }
  return data.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
}

export function buildAppCredentialSelectOptions(configs: AppCredentialConfig[]): SelectOption[] {
  return configs.map(config => ({
    value: config.appCredentialId,
    label: formatAppCredentialLabel(config),
  }));
}

export function buildMerchantSelectOptions(configs: MerchantConfig[]): SelectOption[] {
  return configs.map(config => ({
    value: config.merchantConfigId,
    label: formatMerchantLabel(config),
  }));
}

export function findAppCredentialConfig(
  id: unknown,
  pool: AppCredentialConfig[],
): AppCredentialConfig | undefined {
  const numericId = toNumberId(id);
  if (numericId === undefined) {
    return undefined;
  }
  return pool.find(item => item.appCredentialId === numericId);
}

export function findMerchantConfig(id: unknown, pool: MerchantConfig[]): MerchantConfig | undefined {
  const numericId = toNumberId(id);
  if (numericId === undefined) {
    return undefined;
  }
  return pool.find(item => item.merchantConfigId === numericId);
}

export function isRouteConfigCompleteWithPools(
  route: PlatformPaymentRoute,
  appCredentials: AppCredentialConfig[],
  merchants: MerchantConfig[],
): boolean {
  const appCredential = findAppCredentialConfig(route.appCredentialId, appCredentials);
  const merchant = findMerchantConfig(route.merchantConfigId, merchants);
  return Boolean(appCredential?.configComplete && merchant?.configComplete);
}

/**
 * 解析路由作用域键：优先后端字段，否则按平台/资质 AppID 推导。
 * 小程序启用路由若无法得到非空作用域，返回 undefined（视为作用域缺失）。
 */
export function resolveRouteScopeKey(
  route: Pick<PlatformPaymentRoute, 'clientPlatform' | 'routeScopeKey' | 'appCredentialId'>,
  appCredentials: AppCredentialConfig[],
): string | undefined {
  const fromRoute = route.routeScopeKey?.trim();
  if (fromRoute) {
    return fromRoute;
  }
  if (!isAppScopedPlatform(route.clientPlatform)) {
    return `PLATFORM:${route.clientPlatform}`;
  }
  const credential = findAppCredentialConfig(route.appCredentialId, appCredentials);
  const appId = credential?.appId?.trim();
  if (!appId) {
    return undefined;
  }
  return `APP:${route.clientPlatform}:${appId}`;
}

export function analyzeRouteScopeStatus(
  routes: PlatformPaymentRoute[],
  appCredentials: AppCredentialConfig[],
): {
  conflictRouteIds: Set<number>;
  emptyScopeRouteIds: Set<number>;
  enabledRouteIdByScope: Map<string, number | null>;
} {
  const conflictRouteIds = new Set<number>();
  const emptyScopeRouteIds = new Set<number>();
  const enabledByScope = new Map<string, number[]>();

  routes.forEach(route => {
    if (!route.enabled) {
      return;
    }
    const routeId = toNumberId(route.routeId);
    const scopeKey = resolveRouteScopeKey(route, appCredentials);
    if (!scopeKey) {
      if (routeId !== undefined) {
        emptyScopeRouteIds.add(routeId);
      }
      return;
    }
    const list = enabledByScope.get(scopeKey) || [];
    if (routeId !== undefined) {
      list.push(routeId);
    }
    enabledByScope.set(scopeKey, list);
  });

  const enabledRouteIdByScope = new Map<string, number | null>();
  enabledByScope.forEach((ids, scopeKey) => {
    if (ids.length > 1) {
      ids.forEach(id => conflictRouteIds.add(id));
      enabledRouteIdByScope.set(scopeKey, null);
      return;
    }
    enabledRouteIdByScope.set(scopeKey, ids[0] ?? null);
  });

  return { conflictRouteIds, emptyScopeRouteIds, enabledRouteIdByScope };
}

export function getRouteScopeIssue(
  route: PlatformPaymentRoute,
  status: ReturnType<typeof analyzeRouteScopeStatus>,
): RouteScopeIssue | null {
  const routeId = toNumberId(route.routeId);
  if (routeId === undefined) {
    return null;
  }
  if (status.conflictRouteIds.has(routeId)) {
    return 'conflict';
  }
  if (status.emptyScopeRouteIds.has(routeId)) {
    return 'empty_scope';
  }
  return null;
}

/** 启用保存时携带的当前作用域启用路由 ID（无则为 null）。冲突时返回 null。 */
export function findExpectedActiveRouteId(
  routes: PlatformPaymentRoute[],
  route: Pick<PlatformPaymentRoute, 'clientPlatform' | 'routeScopeKey' | 'appCredentialId'>,
  appCredentials: AppCredentialConfig[],
): number | null {
  const scopeKey = resolveRouteScopeKey(route, appCredentials);
  if (!scopeKey) {
    return null;
  }
  const enabledIds = routes
    .filter(item => item.enabled && resolveRouteScopeKey(item, appCredentials) === scopeKey)
    .map(item => toNumberId(item.routeId))
    .filter((id): id is number => id !== undefined);
  if (enabledIds.length === 1) {
    return enabledIds[0];
  }
  return null;
}

/**
 * 同平台+真实 AppID 是否已存在其他资质（多服务商候选门禁写死关闭时使用）。
 */
export function findExistingCredentialForSameApp(
  configs: AppCredentialConfig[],
  draft: Pick<AppCredentialConfig, 'clientPlatform' | 'appId'> & { appCredentialId?: number },
): AppCredentialConfig | undefined {
  const appId = draft.appId?.trim();
  if (!appId || !draft.clientPlatform) {
    return undefined;
  }
  return configs.find(existing => {
    if (draft.appCredentialId !== undefined && idsEqual(existing.appCredentialId, draft.appCredentialId)) {
      return false;
    }
    return existing.clientPlatform === draft.clientPlatform && existing.appId?.trim() === appId;
  });
}

export function getMultiProviderGateMessage(): string {
  return '多服务商路由功能尚未开放，同一平台和真实 AppID 暂不可创建第二服务商候选资质。';
}

const compareRoutePriority = (left: PlatformPaymentRoute, right: PlatformPaymentRoute) => {
  const leftOrder = left.sortOrder ?? 0;
  const rightOrder = right.sortOrder ?? 0;
  if (leftOrder !== rightOrder) {
    return leftOrder - rightOrder;
  }
  return (toNumberId(left.routeId) || 0) - (toNumberId(right.routeId) || 0);
};

/**
 * 解析平台级“当前启用路由”展示用。
 * 同一作用域存在多条启用、或启用路由作用域缺失时，不任意挑选，返回 undefined。
 */
export function resolvePlatformSelectedRoute(
  routes: PlatformPaymentRoute[],
  options: {
    enabledRouteId?: number;
    appCredentials: AppCredentialConfig[];
    merchants: MerchantConfig[];
  },
): PlatformPaymentRoute | undefined {
  if (!routes.length) {
    return undefined;
  }

  const scopeStatus = analyzeRouteScopeStatus(routes, options.appCredentials);
  if (scopeStatus.conflictRouteIds.size > 0 || scopeStatus.emptyScopeRouteIds.size > 0) {
    return undefined;
  }

  const enabledRoutes = routes.filter(route => route.enabled);
  const completeEnabled = enabledRoutes
    .filter(route => isRouteConfigCompleteWithPools(route, options.appCredentials, options.merchants))
    .sort(compareRoutePriority);

  if (completeEnabled.length === 1) {
    return completeEnabled[0];
  }
  if (completeEnabled.length > 1) {
    // 多作用域各有一条启用时，平台级不再任意选一条作为唯一生效
    return undefined;
  }

  const enabledRouteId = toNumberId(options.enabledRouteId);
  if (enabledRouteId !== undefined) {
    const byId = routes.find(route => idsEqual(route.routeId, enabledRouteId));
    if (byId && !byId.enabled) {
      return byId;
    }
    if (byId && enabledRoutes.length <= 1) {
      return byId;
    }
  }

  if (enabledRoutes.length === 1) {
    return enabledRoutes[0];
  }

  if (enabledRoutes.length === 0) {
    return [...routes].sort(compareRoutePriority)[routes.length - 1];
  }

  return undefined;
}

export function mergeSelectOptions<T>(
  options: T[],
  pool: T[],
  selectedId: unknown,
  getId: (item: T) => number,
): T[] {
  const selectedNumericId = toNumberId(selectedId);
  if (selectedNumericId === undefined) {
    return options;
  }
  if (options.some(item => getId(item) === selectedNumericId)) {
    return options;
  }
  const selected = pool.find(item => getId(item) === selectedNumericId);
  return selected ? [...options, selected] : options;
}

export function collectPlatformAppCredentials(
  platform: ClientPlatform,
  all: AppCredentialConfig[],
  providerSet: Set<PaymentProvider>,
): AppCredentialConfig[] {
  return uniqueById(
    all.filter(
      config =>
        config.clientPlatform === platform &&
        (!config.paymentProvider || providerSet.has(config.paymentProvider)),
    ),
    config => config.appCredentialId,
  );
}

export function collectPlatformMerchantConfigs(
  all: MerchantConfig[],
  providerSet: Set<PaymentProvider>,
): MerchantConfig[] {
  return uniqueById(
    all.filter(config => providerSet.has(config.paymentProvider)),
    config => config.merchantConfigId,
  );
}

function uniqueById<T>(items: T[], getId: (item: T) => number): T[] {
  const map = new Map<number, T>();
  items.forEach(item => {
    const id = getId(item);
    if (!map.has(id)) {
      map.set(id, item);
    }
  });
  return Array.from(map.values());
}

export function getUniqueAppCredentialConfigs(platforms: PlatformPaymentConfig[]): AppCredentialConfig[] {
  return uniqueById(
    platforms.flatMap(platform => platform.appCredentialConfigs || []),
    config => config.appCredentialId,
  );
}

export function getUniqueMerchantConfigs(platforms: PlatformPaymentConfig[]): MerchantConfig[] {
  return uniqueById(
    platforms.flatMap(platform => platform.merchantConfigs || []),
    config => config.merchantConfigId,
  );
}
